import { v4 as uuidv4 } from 'uuid';
import { WorkingHour } from '../features/invoice/invoice.service';
import { useEffect } from 'react';

// Deduction type constants
export const DEDUCTION_TYPES = {
  MILEAGE_REPORT: 'mileage_report',
  TRAVEL_EXPENSES_PDF: 'travel_expenses_pdf',
  EXPENSES_MATERIALS_TOOLS_PDF: 'expenses_materials_tools_pdf',
  ALLOWANCE_OUTSIDE_SWEDEN: 'allowance_outside_sweden',
  ALLOWANCE_IN_SWEDEN: 'allowance_in_sweden',
};

// Article numbers mapping (same as in common/constants.js)
export const ARTICLE_NUMBERS = {
  MILEAGE_TAX_FREE: '87',
  MILEAGE_WITH_TAX: '88',
  ALLOWANCE_IN_TAX_FREE_HALF_DAY: '81',
  ALLOWANCE_IN_WITH_TAX_HALF_DAY: '82',
  ALLOWANCE_IN_TAX_FREE_FULL_DAY: '81',
  ALLOWANCE_IN_WITH_TAX_FULL_DAY: '82',
  ALLOWANCE_OUT_TAX_FREE: '85',
  ALLOWANCE_OUT_WITH_TAX: '86',
  TRAVEL: '91',
  MATERIAL_VAT_TAX_FREE: '2',
  MATERIAL_VAT_WITH_TAX: '1',
  MATERIAL_VAT_WITH_TAX_WITHOUT_PDF: '1',
};

// Amount per unit constants
const AMOUNT_PER_UNIT = {
  MILEAGE: 2.5,
  ALLOWANCE_IN_HALF_DAY: 150,
  ALLOWANCE_IN_FULL_DAY: 300,
};

/**
 * Calculate difference in days between two dates
 */
const calculateDaysDifference = (startDate: string, endDate: string): number => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end day
  
  return diffDays;
};

/**
 * Convert file to base64 string
 */
export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result as string;
      // Remove the data:application/pdf;base64, prefix
      const base64Data = base64String.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Transform deduction data from modal format to WorkingHour format for API
 */
export const transformDeductionToWorkingHour = async (
  deductionData: any,
  vat: number,
  currencyRate: string | number = 1
): Promise<WorkingHour> => {
  const { deductionType, files } = deductionData;
  const currencyGlobalValue = Number(currencyRate);
  // Convert file to base64 if present
  let fileContent = '';
  let fileName = '';
  if (files && files.length > 0) {
    const file = files[0];
    fileContent = await convertFileToBase64(file);
    fileName = file.name;
  }

  // Common base structure
  const baseWorkingHour: Partial<WorkingHour> = {
    // In edit=true mode the original working-hour record comes with a non-zero id.
    // Preserve it so the backend updates the existing row instead of inserting a new one.
    id: (deductionData.id as number) || 0,
    dateId: '', // Will be set from workingDate in each case
    workingHour: 0,
    houseworkType: '',
    fileName: fileName,
    fileContent: fileContent,
    articleId: 0,
    // Preserve timeSheetWorkingHourId so the backend can match and update
    // the original timesheet record instead of inserting a new row
    timeSheetWorkingHourId: (deductionData.timeSheetWorkingHourId as number) || 0,
  } as any;

  switch (deductionType) {
    case DEDUCTION_TYPES.MILEAGE_REPORT: {
      // Handle multiple formats: boolean true, string '1', string 'yes', number 1
      const isTaxFree = deductionData.isTaxFree === true || 
                        deductionData.isTaxFree === 1 || 
                        deductionData.isTaxFree === '1' || 
                        deductionData.isTaxFree === 'yes';
      
      const distance = parseFloat(deductionData.distance) || 0;
      const amountPerUnitSek = AMOUNT_PER_UNIT.MILEAGE;
      const amount = distance * amountPerUnitSek;

      const workingDate = deductionData.travelDate || new Date().toISOString().split('T')[0];
      const workingHour = {
        ...baseWorkingHour,
        workingDate: workingDate,
        dateId: `${workingDate}-${uuidv4()}`,
        number: distance,
        unit: 'Km',
        vat: vat,
        isArticleShownInInvoice: deductionData.showOnInvoice !== 'no' && deductionData.showOnInvoice !== '0',
        amountPerUnit: amountPerUnitSek / currencyGlobalValue,
        amountPerUnitSek: amountPerUnitSek,
        amount: amount / currencyGlobalValue,
        articleNumber: isTaxFree ? ARTICLE_NUMBERS.MILEAGE_TAX_FREE : ARTICLE_NUMBERS.MILEAGE_WITH_TAX,
        workingDescription: 'Mileage Report',
        isTaxFreeArticle: isTaxFree,
        isPdfAttachedToInvoice: deductionData.isPdfAttachedToInvoice === 'yes' || deductionData.isPdfAttachedToInvoice === '1',
        tripStartedPlace: deductionData.tripStartPlace || '',
        tripEndedPlace: deductionData.tripEndPlace || '',
        distance: distance,
        country: '',
        allowancePerDay: 0,
        reasonForTrip: deductionData.reason || '',
        carRegistrationNo: deductionData.carRegistration || '',
        tripStartedDate: deductionData.travelDate || new Date().toISOString().split('T')[0],
        tripEndedDate: deductionData.travelDate || new Date().toISOString().split('T')[0],
        totalDayOfTravels: 0,
        allowanceDestination: '',
        expenseCategory: '',
        amountInSek: amount,
        isHalfDay: '0',
        foodAllowance: 0,
      } as WorkingHour;

     

      return workingHour;
    }

    case DEDUCTION_TYPES.TRAVEL_EXPENSES_PDF: {
      const amountExVat = parseFloat(deductionData.amountExVat) || 0;
      const vatRate = parseFloat(deductionData.vat) || 0;
      const vatAmount = (amountExVat * vatRate) / 100;
      const totalWithVat = amountExVat + vatAmount;

      // Use entered VAT value from form (deductionData.vat) as these deductions have their own VAT
      const finalVat = deductionData.vat != null ? Number(deductionData.vat) : vat;

      const workingDate = deductionData.travelDate || new Date().toISOString().split('T')[0];
      const workingHour = {
        ...baseWorkingHour,
        workingDate: workingDate,
        dateId: `${workingDate}-${uuidv4()}`,
        number: 1,
        unit: 'Pc',
        vat: finalVat,
        isArticleShownInInvoice: deductionData.showOnInvoice !== 'no' && deductionData.showOnInvoice !== '0',
        amountPerUnit: amountExVat / currencyGlobalValue,
        amountPerUnitSek: amountExVat,
        amount: amountExVat / currencyGlobalValue,
        articleNumber: ARTICLE_NUMBERS.TRAVEL,
        workingDescription: 'Travel Expenses as per attached PDF',
        isTaxFreeArticle: true,
        isPdfAttachedToInvoice: deductionData.isPdfAttachedToInvoice === 'yes' || deductionData.isPdfAttachedToInvoice === '1',
        tripStartedPlace: '',
        tripEndedPlace: '',
        distance: 0,
        country: '',
        allowancePerDay: 0,
        reasonForTrip: '',
        carRegistrationNo: '',
        tripStartedDate: deductionData.travelDate || new Date().toISOString().split('T')[0],
        tripEndedDate: deductionData.travelDate || new Date().toISOString().split('T')[0],
        totalDayOfTravels: 0,
        allowanceDestination: '',
        expenseCategory: '',
        amountInSek: totalWithVat,
        isHalfDay: '0',
        foodAllowance: 0,
      } as WorkingHour;

   

      return workingHour;
    }

    case DEDUCTION_TYPES.EXPENSES_MATERIALS_TOOLS_PDF: {
      // Handle multiple formats: boolean true, string '1', string 'yes', number 1
      const isTaxFree = deductionData.isTaxFree === true || 
                        deductionData.isTaxFree === 1 || 
                        deductionData.isTaxFree === '1' || 
                        deductionData.isTaxFree === 'yes';
      
      const amountExVat = parseFloat(deductionData.amountExVat) || 0;
      const vatRate = parseFloat(deductionData.vat) || 0;
      const vatAmount = (amountExVat * vatRate) / 100;
      const totalWithVat = amountExVat + vatAmount;

      // Determine article number based on tax-free status only
      const articleNumber = isTaxFree ? ARTICLE_NUMBERS.MATERIAL_VAT_TAX_FREE : ARTICLE_NUMBERS.MATERIAL_VAT_WITH_TAX;

      // Use entered VAT value from form (deductionData.vat) as these deductions have their own VAT
      const finalVat = deductionData.vat != null ? Number(deductionData.vat) : vat;

      const workingDate = deductionData.dateOfPurchase || new Date().toISOString().split('T')[0];
      const workingHour = {
        ...baseWorkingHour,
        workingDate: workingDate,
        dateId: `${workingDate}-${uuidv4()}`,
        number: 1,
        unit: 'Pc',
        vat: finalVat,
        isArticleShownInInvoice: deductionData.showOnInvoice !== 'no' && deductionData.showOnInvoice !== '0',
        amountPerUnit: amountExVat / currencyGlobalValue,
        amountPerUnitSek: amountExVat,
        amount: amountExVat / currencyGlobalValue,
        articleNumber: articleNumber,
        workingDescription: 'Expenses for materials and tools as per attached PDF',
        isTaxFreeArticle: isTaxFree,
        isPdfAttachedToInvoice: deductionData.isPdfAttachedToInvoice === 'yes' || deductionData.isPdfAttachedToInvoice === '1',
        tripStartedPlace: '',
        tripEndedPlace: '',
        distance: 0,
        country: '',
        allowancePerDay: 0,
        reasonForTrip: '',
        carRegistrationNo: '',
        tripStartedDate: deductionData.dateOfPurchase || new Date().toISOString().split('T')[0],
        tripEndedDate: deductionData.dateOfPurchase || new Date().toISOString().split('T')[0],
        totalDayOfTravels: 0,
        allowanceDestination: '',
        expenseCategory: '',
        amountInSek: totalWithVat,
        isHalfDay: '0',
        foodAllowance: 0,
      } as WorkingHour;


      return workingHour;
    }

    case DEDUCTION_TYPES.ALLOWANCE_OUTSIDE_SWEDEN: {
      // Handle multiple formats: boolean true, string '1', string 'yes', number 1
      const isTaxFree = deductionData.isTaxFree === true || 
                        deductionData.isTaxFree === 1 || 
                        deductionData.isTaxFree === '1' || 
                        deductionData.isTaxFree === 'yes';
      
      const tripStartDate = deductionData.tripStartDate;
      const tripEndDate = deductionData.tripEndDate;
      const travelDays = calculateDaysDifference(tripStartDate, tripEndDate);
      
      // Get allowance per day from the form data (it should be calculated and passed from the modal)
      const allowancePerDay = parseFloat(deductionData.allowancePerDay) || 450.00;
      const totalAmount = travelDays * allowancePerDay;

      const workingDate = tripStartDate || new Date().toISOString().split('T')[0];
      const workingHour = {
        ...baseWorkingHour,
        workingDate: workingDate,
        dateId: `${workingDate}-${uuidv4()}`,
        number: travelDays,
        unit: 'Day',
        vat: vat,
        isArticleShownInInvoice: deductionData.showOnInvoice !== 'no' && deductionData.showOnInvoice !== '0',
        amountPerUnit: allowancePerDay / currencyGlobalValue,
        amountPerUnitSek: allowancePerDay,
        amount: totalAmount / currencyGlobalValue,
        articleNumber: isTaxFree ? ARTICLE_NUMBERS.ALLOWANCE_OUT_TAX_FREE : ARTICLE_NUMBERS.ALLOWANCE_OUT_WITH_TAX,
        workingDescription: 'Allowance outside Sweden',
        isTaxFreeArticle: isTaxFree,
        isPdfAttachedToInvoice: deductionData.isPdfAttachedToInvoice === 'yes' || deductionData.isPdfAttachedToInvoice === '1',
        tripStartedPlace: '',
        tripEndedPlace: '',
        distance: 0,
        country: deductionData.country || '',
        allowancePerDay: allowancePerDay,
        reasonForTrip: deductionData.reason || '',
        carRegistrationNo: '',
        tripStartedDate: tripStartDate || new Date().toISOString().split('T')[0],
        tripEndedDate: tripEndDate || new Date().toISOString().split('T')[0],
        totalDayOfTravels: travelDays,
        allowanceDestination: deductionData.destination || '',
        expenseCategory: '',
        amountInSek: totalAmount,
        isHalfDay: '0',
        foodAllowance: parseFloat(deductionData.foodAllowance) || 0,
      } as WorkingHour;

    
      return workingHour;
    }

    case DEDUCTION_TYPES.ALLOWANCE_IN_SWEDEN: {
      // Handle multiple formats: boolean true, string '1', string 'yes', number 1
      const isTaxFree = deductionData.isTaxFree === true || 
                        deductionData.isTaxFree === 1 || 
                        deductionData.isTaxFree === '1' || 
                        deductionData.isTaxFree === 'yes';
      
      const isPartialWorkday = deductionData.isPartialWorkday === 'yes' || deductionData.isPartialWorkday === '1';
      const tripStartDate = deductionData.tripStartDate;
      const tripEndDate = deductionData.tripEndDate;
      const travelDays = calculateDaysDifference(tripStartDate, tripEndDate);
      
      // Allowance per day for Sweden (150 for half day, 300 for full day)
      const allowancePerDay = parseFloat(deductionData.allowancePerDay) || 150.00;
      const totalAmount = travelDays * allowancePerDay;

      // Article number based only on tax-free status (same for half/full day)
      // 81 = tax-free, 82 = with VAT
      const articleNumber = isTaxFree ? ARTICLE_NUMBERS.ALLOWANCE_IN_TAX_FREE_FULL_DAY : ARTICLE_NUMBERS.ALLOWANCE_IN_WITH_TAX_FULL_DAY;

      const workingDate = tripStartDate || new Date().toISOString().split('T')[0];
      const workingHour = {
        ...baseWorkingHour,
        workingDate: workingDate,
        dateId: `${workingDate}-${uuidv4()}`,
        number: travelDays,
        unit: 'Day',
        vat: vat,
        isArticleShownInInvoice: deductionData.showOnInvoice !== 'no' && deductionData.showOnInvoice !== '0',
        amountPerUnit: allowancePerDay / currencyGlobalValue,
        amountPerUnitSek: allowancePerDay,
        amount: totalAmount / currencyGlobalValue,
        articleNumber: articleNumber,
        workingDescription: 'Allowance in Sweden',
        isTaxFreeArticle: isTaxFree,
        isPdfAttachedToInvoice: deductionData.isPdfAttachedToInvoice === 'yes' || deductionData.isPdfAttachedToInvoice === '1',
        tripStartedPlace: '',
        tripEndedPlace: '',
        distance: 0,
        country: 'Sweden',
        allowancePerDay: allowancePerDay,
        reasonForTrip: deductionData.reason || '',
        carRegistrationNo: '',
        tripStartedDate: tripStartDate || new Date().toISOString().split('T')[0],
        tripEndedDate: tripEndDate || new Date().toISOString().split('T')[0],
        totalDayOfTravels: travelDays,
        allowanceDestination: deductionData.destination || '',
        expenseCategory: '',
        amountInSek: totalAmount,
        isHalfDay: isPartialWorkday ? '1' : '0',
        foodAllowance: parseFloat(deductionData.foodAllowance) || 0,
      } as WorkingHour;

     
      return workingHour;
    }

    default:
      throw new Error(`Unknown deduction type: ${deductionType}`);
  }
};

/**
 * Transform multiple deductions to WorkingHour array
 */
export const transformDeductionsToWorkingHours = async (
  deductions: any[],
  vat: number,
  currencyRate: string | number = 1
): Promise<WorkingHour[]> => {
  const workingHours: WorkingHour[] = [];
  
  for (const deduction of deductions) {
    try {
      const workingHour = await transformDeductionToWorkingHour(deduction, vat, currencyRate);
      workingHours.push(workingHour);
    } catch (error) {
      console.error('Error transforming deduction:', error, deduction);
      // Continue with other deductions even if one fails
    }
  }
  
  return workingHours;
};
